import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, SHIPPING_CARRIERS } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Trash2, ShoppingBag, Truck, Tag, CheckCircle, XCircle, Clock, MapPin, Loader2, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { resolveImageUrl } from '../utils/imageUrl';
import { CartUpsell } from '../components/CartUpsell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal, 
    getShippingCost, 
    getCartCount, 
    applyCoupon, 
    coupon, 
    removeCoupon,
    selectedCarrier,
    selectCarrier,
    liveRates,
    liveCarrier,
    selectLiveCarrier,
    setRates,
    getSelectedCarrier
  } = useCart();
  
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [ratesLoading, setRatesLoading] = useState(false);

  const subtotal = getCartTotal();
  const shippingCost = getShippingCost();
  const isFreeShipping = subtotal >= 80 || (coupon && coupon.discount_type === 'free_shipping');
  
  // Calculate discount from coupon
  const couponDiscount = coupon ? coupon.discount_amount : 0;
  const total = subtotal + shippingCost - couponDiscount;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Ingresa un código de cupón');
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    setCouponSuccess('');

    try {
      const response = await axios.post(`${API}/coupons/validate?code=${couponCode}&cart_total=${subtotal}`);
      
      if (response.data.valid) {
        applyCoupon({
          code: couponCode.toUpperCase(),
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value,
          discount_amount: response.data.discount_amount
        });
        setCouponSuccess(response.data.message);
        setCouponError('');
      } else {
        setCouponError(response.data.message);
        setCouponSuccess('');
      }
    } catch (error) {
      setCouponError('Error al validar el cupón');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    setCouponCode('');
    setCouponSuccess('');
    setCouponError('');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-white text-black flex flex-col">
        <Header onCartClick={() => navigate('/cart')} />
        <div className="flex-1 max-w-[1440px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-md mx-auto text-center">
            <ShoppingBag className="w-16 h-16 mx-auto mb-8 text-neutral-300" strokeWidth={1.2} />
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Vacío
            </p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-4">
              Tu carrito está vacío
            </h2>
            <p className="text-sm text-neutral-600 mb-8">
              ¡Empieza a agregar productos de nuestra colección!
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-black text-white hover:bg-neutral-800 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
            >
              Ver catálogo
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header onCartClick={() => navigate('/cart')} />
      <div className="flex-1">
      
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Seguir comprando
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-4">
            <h1 className="text-3xl font-bold mb-6">Carrito de compras</h1>
            
            {cart.map((item, index) => (
              <Card key={`${item.garment_id}-${item.size}-${item.color}-${index}`} className="p-4" data-testid={`cart-item-${index}`}>
                <div className="flex gap-4">
                  <img
                    src={resolveImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-24 h-24 object-contain rounded-lg bg-gray-50"
                  />
                  
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{item.name}</h3>
                    <p className="text-sm text-gray-600">Talla: {item.size} | Color: {item.color}</p>
                    <p className="text-lg font-bold text-black mt-2">{item.price.toFixed(2)}€</p>
                  </div>

                  <div className="flex flex-col items-end justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.garment_id, item.size, item.color)}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.garment_id, item.size, item.color, item.quantity - 1)}
                        data-testid={`decrease-quantity-${index}`}
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.garment_id, item.size, item.color, parseInt(e.target.value) || 1)}
                        className="w-16 text-center"
                        min="1"
                        data-testid={`quantity-input-${index}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.garment_id, item.size, item.color, item.quantity + 1)}
                        data-testid={`increase-quantity-${index}`}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {/* Shipping Carrier Selection */}
            <Card className="p-6 mt-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-black" />
                Método de envío
              </h2>
              
              {isFreeShipping && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-green-700 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    ¡Envío gratis en todos los transportistas!
                  </p>
                </div>
              )}

              {/* Postal code input for live rates */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Código postal de destino"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="flex-1"
                    data-testid="shipping-postal-code"
                  />
                  <Button
                    onClick={async () => {
                      if (postalCode.length < 4) return;
                      setRatesLoading(true);
                      try {
                        const res = await axios.get(`${API}/shipping/rates?postal_code=${postalCode}&items_count=${getCartCount()}`);
                        setRates(res.data.rates);
                        if (res.data.rates.length > 0) {
                          selectLiveCarrier(res.data.rates[0]);
                        }
                      } catch { }
                      setRatesLoading(false);
                    }}
                    disabled={ratesLoading || postalCode.length < 4}
                    variant="outline"
                    data-testid="shipping-calc-btn"
                  >
                    {ratesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    <span className="ml-1 hidden sm:inline">Calcular</span>
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Live rates from Packlink */}
                {liveRates && liveRates.length > 0 ? (
                  liveRates.map((rate) => {
                    const isSelected = liveCarrier?.id === rate.id;
                    return (
                      <div
                        key={rate.id}
                        onClick={() => selectLiveCarrier(rate)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected ? 'border-black bg-neutral-50' : 'border-gray-200 hover:border-neutral-300'
                        }`}
                        data-testid={`carrier-${rate.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-black' : 'border-gray-300'
                            }`}>
                              {isSelected && <div className="w-3 h-3 rounded-full bg-black" />}
                            </div>
                            <div>
                              <p className="font-semibold">📦 {rate.carrier}</p>
                              <p className="text-sm text-gray-500">{rate.service_name}</p>
                              {rate.delivery_time && (
                                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3" /> {rate.delivery_time}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {isFreeShipping ? (
                              <div>
                                <span className="text-gray-400 line-through text-sm">{rate.price.toFixed(2)}€</span>
                                <p className="text-green-600 font-bold">GRATIS</p>
                              </div>
                            ) : (
                              <span className="font-bold text-lg">{rate.price.toFixed(2)}€</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  /* Fallback: static carriers */
                  Object.values(SHIPPING_CARRIERS).map((carrier) => (
                    <div
                      key={carrier.id}
                      onClick={() => { selectCarrier(carrier.id); selectLiveCarrier(null); }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        !liveCarrier && selectedCarrier === carrier.id
                          ? 'border-black bg-neutral-50'
                          : 'border-gray-200 hover:border-neutral-300'
                      }`}
                      data-testid={`carrier-${carrier.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            !liveCarrier && selectedCarrier === carrier.id ? 'border-black' : 'border-gray-300'
                          }`}>
                            {!liveCarrier && selectedCarrier === carrier.id && (
                              <div className="w-3 h-3 rounded-full bg-black" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">{carrier.icon} {carrier.name}</p>
                            <p className="text-sm text-gray-500">{carrier.description}</p>
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" />
                              {carrier.deliveryTime}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {isFreeShipping ? (
                            <div>
                              <span className="text-gray-400 line-through text-sm">{carrier.price.toFixed(2)}€</span>
                              <p className="text-green-600 font-bold">GRATIS</p>
                            </div>
                          ) : (
                            <span className="font-bold text-lg">{carrier.price.toFixed(2)}€</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {!liveRates && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Introduce tu código postal para ver tarifas reales
                  </p>
                )}
              </div>
            </Card>

            <CartUpsell cart={cart} subtotal={subtotal} />
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-4">
              <h2 className="text-2xl font-black uppercase tracking-tight mb-6">Resumen del pedido</h2>
              
              {/* Coupon Section */}
              <div className="mb-6" data-testid="cart-discount-section">
                <p className="text-sm font-black uppercase tracking-tight mb-3">Código de descuento</p>
                
                {!coupon ? (
                  <>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Introducir código"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 uppercase rounded-none h-12"
                        data-testid="coupon-input"
                      />
                      <Button
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="bg-black hover:bg-neutral-800 text-white rounded-none h-12 px-6"
                        data-testid="apply-coupon-button"
                      >
                        {couponLoading ? '...' : 'Aplicar'}
                      </Button>
                    </div>
                    <p className="text-xs text-neutral-500 mt-2" data-testid="gift-card-hint">Los códigos de tarjeta regalo se aplican al finalizar la compra.</p>
                    
                    {couponError && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <XCircle className="w-4 h-4" />
                        {couponError}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="font-semibold text-green-700">{coupon.code}</span>
                        <p className="text-xs text-green-600">{couponSuccess}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveCoupon}
                      className="text-red-500 hover:text-red-700"
                      data-testid="remove-coupon-button"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal ({getCartCount()} artículos)</span>
                  <span className="font-semibold">{subtotal.toFixed(2)}€</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center gap-2">
                    <span>{getSelectedCarrier().icon}</span>
                    {getSelectedCarrier().name}
                  </span>
                  <span className={isFreeShipping ? 'text-green-600 font-semibold' : 'font-semibold'}>
                    {isFreeShipping ? '¡GRATIS!' : `${shippingCost.toFixed(2)}€`}
                  </span>
                </div>

                {coupon && coupon.discount_amount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Descuento ({coupon.code})
                    </span>
                    <span className="font-semibold">-{coupon.discount_amount.toFixed(2)}€</span>
                  </div>
                )}

                {subtotal < 80 && !isFreeShipping && (
                  <div className="text-sm" data-testid="free-shipping-progress">
                    <p className="mb-2">Añade <strong>{(80 - subtotal).toFixed(2)}€</strong> más para envío gratis</p>
                    <div className="h-1.5 bg-neutral-200"><div className="h-full bg-black transition-[width] duration-500" style={{ width: `${Math.min(100, (subtotal / 80) * 100)}%` }} /></div>
                  </div>
                )}
                {isFreeShipping && (
                  <p className="text-sm text-green-700 font-semibold" data-testid="free-shipping-unlocked">¡Tienes envío gratis!</p>
                )}

                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-black">{total.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-black hover:bg-neutral-800 text-base py-6 rounded-none font-bold"
                data-testid="checkout-button"
              >
                <Lock className="w-4 h-4 mr-2" /> Paga de forma segura
              </Button>

              <div className="flex items-center justify-center gap-2 mt-4" data-testid="cart-payment-logos">
                <img src="/logos/visa.svg" alt="Visa" className="h-6 border border-neutral-200" />
                <img src="/logos/mastercard.svg" alt="Mastercard" className="h-6 border border-neutral-200 px-1" />
                <img src="/logos/paypal.svg" alt="PayPal" className="h-6 border border-neutral-200 px-1" />
                <span className="h-6 px-2 bg-black text-white text-[11px] font-semibold flex items-center">Apple Pay</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default CartPage;
